/**
 * Diagramação padrão das páginas (piloto no Peer Group, 2026-10-02):
 *
 * - `Pagina`: conteúdo com largura máxima de 1.600 px, centralizado;
 * - `CabecalhoPagina`: caminho (Seção › Página), título, descrição e, à direita, data de
 *   referência, fonte dos dados e ações da página;
 * - `Secao`: cada bloco da página num cartão branco com título, legenda e ações.
 */
import { Anchor, Box, Breadcrumbs, Group, Paper, Stack, Text, Title } from '@mantine/core';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router';

import { PAGINAS, rotaNova } from '../app/paginas';
import { formatarData } from '../lib/formatos';

export const LARGURA_MAXIMA = 1600;

export function Pagina({ children }: { children: ReactNode }) {
  return (
    <Box maw={LARGURA_MAXIMA} mx="auto" w="100%">
      <Stack gap="lg">{children}</Stack>
    </Box>
  );
}

export function CabecalhoPagina({
  titulo,
  descricao,
  dadosAte,
  fonte,
  acoes,
  children,
}: {
  titulo: string;
  descricao?: ReactNode;
  /** Data do dado mais recente (ISO). */
  dadosAte?: string | null;
  /** De onde vêm os dados (ex.: "CVM · Fibery"). */
  fonte?: string;
  acoes?: ReactNode;
  /** Linha extra abaixo da descrição (ex.: grupos selecionados). */
  children?: ReactNode;
}) {
  const { pathname } = useLocation();
  const pagina = PAGINAS.find((p) => rotaNova(p) === pathname);
  return (
    <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
      <Stack gap={4}>
        {pagina && (
          <Breadcrumbs separator="›" separatorMargin={6}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              {pagina.secao}
            </Text>
            <Anchor component={Link} to={pathname} size="xs" c="dimmed" tt="uppercase" fw={600}>
              {pagina.titulo}
            </Anchor>
          </Breadcrumbs>
        )}
        <Title order={2} className="titulo-marca" c="#19202A">
          {titulo}
        </Title>
        {descricao && (
          <Text size="sm" c="dimmed" maw={760}>
            {descricao}
          </Text>
        )}
        {children}
      </Stack>
      <Stack gap={6} align="flex-end">
        {acoes}
        {(dadosAte || fonte) && (
          <Text size="xs" c="dimmed" ta="right">
            {dadosAte && (
              <>
                Dado mais recente: <b>{formatarData(dadosAte)}</b>
              </>
            )}
            {dadosAte && fonte && ' · '}
            {fonte && <>Fonte: {fonte}</>}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

export function Secao({
  titulo,
  legenda,
  acoes,
  children,
}: {
  titulo: string;
  legenda?: ReactNode;
  acoes?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper withBorder radius="md" p="lg" bg="white">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={2}>
            <Title order={4} className="titulo-marca" c="#19202A">
              {titulo}
            </Title>
            {legenda && (
              <Text size="xs" c="dimmed">
                {legenda}
              </Text>
            )}
          </Stack>
          {acoes}
        </Group>
        {children}
      </Stack>
    </Paper>
  );
}
